import { BooLogo } from "@/components/BooLogo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { Calendar, Check, Mail } from "lucide-react";
import type { ReactNode } from "react";
import type { CosAppKind } from "./apps";
import { initialOf } from "./types";

// Presentational pieces of the chief-of-staff onboarding branch. Copy rules
// (Apple HIG Writing, WWDC22 "Writing for interfaces", Airbnb): the headline
// carries the message, body text only adds what it cannot, one idea per
// screen said once where the user acts.

// Who this Boo works for. Read-only: the person onboarding is the principal.
export function PrincipalCard({ name, email }: { name: string; email: string }) {
  return (
    <div className="space-y-1.5">
      <Label>Works for</Label>
      <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/30 px-4 py-2.5">
        <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-foreground text-sm font-medium text-background">
          {initialOf(name)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{name.trim() || "You"}</div>
          <div className="truncate text-xs text-muted-foreground">{email}</div>
        </div>
        <span className="rounded-full border border-border bg-background px-2 py-0.5 text-[11px] text-muted-foreground">
          You
        </span>
      </div>
    </div>
  );
}

const TIME_ZONES = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Warsaw",
  "Asia/Dubai",
  "Asia/Singapore",
  "Asia/Tokyo",
  "Australia/Sydney",
];

// Time zone (browser-detected, editable) and the hour the morning briefing
// lands. Both go to the brain as setup inputs on the container create.
export function TimeZoneBriefingFields({
  timeZone,
  briefingTime,
  onTimeZone,
  onBriefingTime,
  disabled,
}: {
  timeZone: string;
  briefingTime: string;
  onTimeZone: (value: string) => void;
  onBriefingTime: (value: string) => void;
  disabled?: boolean;
}) {
  const zones = TIME_ZONES.includes(timeZone) ? TIME_ZONES : [timeZone, ...TIME_ZONES];
  return (
    <div className="grid grid-cols-[1fr_auto] gap-2">
      <div className="space-y-1.5">
        <Label htmlFor="cos-time-zone">Time zone</Label>
        <Select value={timeZone} onValueChange={onTimeZone} disabled={disabled}>
          <SelectTrigger id="cos-time-zone" className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {zones.map((zone) => (
              <SelectItem key={zone} value={zone}>
                {zone.replace(/_/g, " ")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="cos-briefing-time">Briefing time</Label>
        <Input
          id="cos-briefing-time"
          type="time"
          value={briefingTime}
          onChange={(e) => onBriefingTime(e.target.value)}
          disabled={disabled}
          className="w-[7.5rem]"
        />
      </div>
    </div>
  );
}

// The two needs (inbox, calendar) as the teammate flow's target dots, keyed
// by need instead of count: an inbox from any provider fills the first, a
// calendar the second.
export function NeedDots({ kinds }: { kinds: Set<CosAppKind> }) {
  const needs: Array<{ kind: CosAppKind; icon: ReactNode }> = [
    { kind: "inbox", icon: <Mail className="size-3.5" /> },
    { kind: "calendar", icon: <Calendar className="size-3.5" /> },
  ];
  return (
    <div className="flex items-center gap-2">
      {needs.map(({ kind, icon }) => {
        const done = kinds.has(kind);
        return (
          <span
            key={kind}
            className={cn(
              "flex size-7 items-center justify-center rounded-full border transition-colors",
              done
                ? "border-emerald-300 bg-emerald-50 text-emerald-600"
                : "border-dashed text-muted-foreground/60",
            )}
          >
            {done ? <Check className="size-3.5" /> : icon}
          </span>
        );
      })}
    </div>
  );
}

// Provisioning wait, compact so the inbox card below fits a laptop viewport
// without scrolling (the teammate flow's shared header is taller).
export function ProvisioningHeader({ booName }: { booName: string }) {
  return (
    <div className="flex flex-col items-center gap-5 py-4 text-center">
      <div className="relative">
        <span
          className="boo-pulse absolute -inset-6 rounded-full"
          style={{
            background:
              "radial-gradient(closest-side, rgba(127, 119, 221, 0.45), transparent 70%)",
          }}
        />
        <BooLogo className="relative size-16 rounded-2xl" />
      </div>
      <div className="space-y-1">
        <h1 className="text-2xl font-semibold">Setting up {booName}…</h1>
        <p className="text-sm text-muted-foreground">About a minute.</p>
      </div>
    </div>
  );
}

// Ready state for the provisioning wait. Headline only: the inbox card
// below carries the action and the Continue row the way forward.
export function ReadyHeader({ booName }: { booName: string }) {
  return (
    <div className="flex flex-col items-center gap-5 py-4 text-center">
      <div className="relative">
        <BooLogo className="size-16 rounded-2xl" />
        <span className="absolute -right-1.5 -bottom-1.5 flex size-7 items-center justify-center rounded-full border-2 border-background bg-emerald-500 text-white">
          <Check className="size-4" />
        </span>
      </div>
      <h1 className="text-2xl font-semibold">{booName} is ready</h1>
    </div>
  );
}
